import type { Id, TeamCount } from './types.ts';
import { TEAM_COUNTS } from './types.ts';

export interface TeamIncompatibilityPair {
  baladinAId: Id;
  baladinBId: Id;
}

export interface TeamAssignmentDraft {
  teamIndex: number;
  baladinId: Id;
}

/** Normalise une paire pour stocker toujours (min, max) lexicographique. */
export function normalizeIncompatibilityPair(a: Id, b: Id): { baladinAId: Id; baladinBId: Id } | null {
  if (!a || !b || a === b) return null;
  return a < b ? { baladinAId: a, baladinBId: b } : { baladinAId: b, baladinBId: a };
}

function incompatibleWith(
  baladinId: Id,
  teammates: Id[],
  banned: Set<string>,
): boolean {
  for (const other of teammates) {
    const key = baladinId < other ? `${baladinId}|${other}` : `${other}|${baladinId}`;
    if (banned.has(key)) return true;
  }
  return false;
}

function buildBanSet(pairs: TeamIncompatibilityPair[]): Set<string> {
  const banned = new Set<string>();
  for (const pair of pairs) {
    const normalized = normalizeIncompatibilityPair(pair.baladinAId, pair.baladinBId);
    if (!normalized) continue;
    banned.add(`${normalized.baladinAId}|${normalized.baladinBId}`);
  }
  return banned;
}

function shuffle<T>(items: T[], random: () => number = Math.random): T[] {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const tmp = next[i]!;
    next[i] = next[j]!;
    next[j] = tmp;
  }
  return next;
}

/**
 * Répartit aléatoirement les enfants présents en `teamCount` équipes en
 * respectant les incompatibilités. Plusieurs tentatives ; en dernier recours,
 * placement dans l'équipe la moins chargée même si une règle est cassée.
 */
export function generateTeams(
  presentBaladinIds: Id[],
  teamCount: TeamCount,
  incompatibilities: TeamIncompatibilityPair[] = [],
  options: { attempts?: number; random?: () => number } = {},
): { assignments: TeamAssignmentDraft[]; violations: Array<[Id, Id]> } {
  if (!(TEAM_COUNTS as readonly number[]).includes(teamCount)) {
    throw new Error('Le nombre d’équipes doit être compris entre 2 et 20.');
  }
  if (presentBaladinIds.length === 0) {
    return { assignments: [], violations: [] };
  }

  const banned = buildBanSet(incompatibilities);
  const attempts = options.attempts ?? 80;
  const random = options.random ?? Math.random;
  const unique = [...new Set(presentBaladinIds)];

  let best: TeamAssignmentDraft[] | null = null;
  let bestViolations: Array<[Id, Id]> = [];

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const order = shuffle(unique, random);
    const teams: Id[][] = Array.from({ length: teamCount }, () => []);
    let failed = false;

    for (const baladinId of order) {
      const ranked = [...teams.keys()].sort((a, b) => {
        const sizeDiff = teams[a]!.length - teams[b]!.length;
        if (sizeDiff !== 0) return sizeDiff;
        return random() < 0.5 ? -1 : 1;
      });
      const fit = ranked.find((index) => !incompatibleWith(baladinId, teams[index]!, banned));
      if (fit == null) {
        failed = true;
        break;
      }
      teams[fit]!.push(baladinId);
    }

    if (!failed) {
      return {
        assignments: flattenTeams(teams),
        violations: [],
      };
    }

    // Tentative de secours pour mémoriser le « moins pire ».
    const fallbackTeams: Id[][] = Array.from({ length: teamCount }, () => []);
    for (const baladinId of order) {
      const ranked = [...fallbackTeams.keys()].sort(
        (a, b) => fallbackTeams[a]!.length - fallbackTeams[b]!.length,
      );
      const preferred =
        ranked.find((index) => !incompatibleWith(baladinId, fallbackTeams[index]!, banned)) ??
        ranked[0]!;
      fallbackTeams[preferred]!.push(baladinId);
    }
    const violations = collectViolations(fallbackTeams, banned);
    if (!best || violations.length < bestViolations.length) {
      best = flattenTeams(fallbackTeams);
      bestViolations = violations;
      if (violations.length === 0) break;
    }
  }

  return {
    assignments: best ?? [],
    violations: bestViolations,
  };
}

function flattenTeams(teams: Id[][]): TeamAssignmentDraft[] {
  const assignments: TeamAssignmentDraft[] = [];
  teams.forEach((members, teamIndex) => {
    for (const baladinId of members) {
      assignments.push({ teamIndex, baladinId });
    }
  });
  return assignments;
}

function collectViolations(teams: Id[][], banned: Set<string>): Array<[Id, Id]> {
  const violations: Array<[Id, Id]> = [];
  for (const members of teams) {
    for (let i = 0; i < members.length; i += 1) {
      for (let j = i + 1; j < members.length; j += 1) {
        const a = members[i]!;
        const b = members[j]!;
        const key = a < b ? `${a}|${b}` : `${b}|${a}`;
        if (banned.has(key)) violations.push(a < b ? [a, b] : [b, a]);
      }
    }
  }
  return violations;
}

/** Vérifie les violations d'incompatibilité d'une répartition existante. */
export function findTeamViolations(
  assignments: TeamAssignmentDraft[],
  incompatibilities: TeamIncompatibilityPair[],
): Array<[Id, Id]> {
  const banned = buildBanSet(incompatibilities);
  const byTeam = new Map<number, Id[]>();
  for (const row of assignments) {
    const list = byTeam.get(row.teamIndex) ?? [];
    list.push(row.baladinId);
    byTeam.set(row.teamIndex, list);
  }
  return collectViolations([...byTeam.values()], banned);
}
